import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { api } from "~/lib/api";

export const fetchUserServer = createServerFn({ method: "GET" }).handler(
  async () => {
    const cookie = getCookie("tory_driver_token");
    const response = await api("/auth/me", { token: cookie ?? undefined });

    try {
      const user = await response.json();

      if (!user?.user_id) {
        return null;
      }

      return user;
    } catch (e) {
      return null;
    }
  },
);

export const loginServer = createServerFn({ method: "POST" })
  .validator((data: { email: string; password: string }) => data)
  .handler(async ({ data }) => {
    const response = await api("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return { error: "Invalid email or password", status: response.status };
    }

    const result = await response.json();

    if (result.access_token) {
      setCookie("tory_driver_token", result.access_token, {
        maxAge: 60 * 60 * 24 * 14,
        path: "/",
        httpOnly: true,
      });
    }

    return result;
  });

export const logoutServer = createServerFn({ method: "POST" }).handler(
  async () => {
    setCookie("tory_driver_token", "", {
      maxAge: 0,
      path: "/",
      httpOnly: true,
    });
    return { success: true };
  },
);

export const getAccessTokenServer = createServerFn({ method: "GET" }).handler(
  async () => {
    return getCookie("tory_driver_token") || null;
  },
);

export const fetchDriverStateServer = createServerFn({ method: "GET" }).handler(
  async () => {
    const cookie = getCookie("tory_driver_token");
    if (!cookie) return null;

    try {
      const response = await api("/drivers/me", { token: cookie });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  },
);

export const forgotPasswordServer = createServerFn({ method: "POST" })
  .validator((data: { email: string }) => data)
  .handler(async ({ data }) => {
    const response = await api("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let detail = "Could not send reset email. Please try again.";
      try {
        const body = await response.json();
        if (body?.detail && typeof body.detail === "string") detail = body.detail;
      } catch {}
      return { error: detail, status: response.status };
    }

    return await response.json();
  });

export const resetPasswordServer = createServerFn({ method: "POST" })
  .validator((data: { token: string; password: string }) => data)
  .handler(async ({ data }) => {
    const response = await api("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let detail = "Invalid or expired reset link";
      try {
        const body = await response.json();
        if (body?.detail && typeof body.detail === "string") detail = body.detail;
      } catch {}
      return { error: detail, status: response.status };
    }

    return await response.json();
  });

export const isPhoneNumberVerified = async (
  phoneNumber: string,
): Promise<boolean> => {
  try {
    const response = await api("/auth/check-phone", {
      method: "POST",
      body: JSON.stringify({ phoneNumber }),
    });

    const data = (await response.json()) as { verified?: boolean };
    return data?.verified || false;
  } catch (error) {
    console.error("Error checking phone number:", error);
    return false;
  }
};
